import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-control-geocoder';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';
import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  Autocomplete,
  Chip,
  Box,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, useMap } from 'react-leaflet';
import { Container } from 'reactstrap';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const StyledTableCell = styled(TableCell)({
  color: 'white !important',
  border: 'solid 1px #dee2e6',
  fontWeight: '800',
  textAlign: 'center',
  backgroundColor: '#47A992',
  padding: 'none',
  whiteSpace: 'nowrap',
});

const FactoryLocationInitialValue = {
  Factory: '',
  Latitude: '',
  Longitude: '',
};

const filterInitialValue = {
  searchTerm: '',
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const GeocoderControl = ({ position, onMarkGeocode }) => {
  const map = useMap();

  useEffect(() => {
    let geocoder = L.Control.Geocoder.nominatim();
    if (typeof URLSearchParams !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const geocoderString = params.get('geocoder');
      if (geocoderString) {
        geocoder = L.Control.Geocoder[geocoderString]();
      }
    }

    const control = L.Control.geocoder({
      position,
      geocoder,
      defaultMarkGeocode: false,
    })
      .on('markgeocode', (e) => {
        onMarkGeocode(e);
        map.setView(e.geocode.center, map.getZoom());
      })
      .addTo(map);

    return () => {
      map.removeControl(control);
    };
  }, [map, position, onMarkGeocode]);

  return null;
};

const LocationMarker = ({ values, setFieldValue }) => {
  useMapEvents({
    click(e) {
      setFieldValue('Latitude', e.latlng.lat?.toFixed(6));
      setFieldValue('Longitude', e.latlng.lng?.toFixed(6));
    },
  });

  return values.Latitude && values.Longitude ? (
    <Marker
      draggable
      position={{
        lat: values.Latitude || 20.5937,
        lng: values.Longitude || 78.9629,
      }}
      eventHandlers={{
        dragend: (e) => {
          const latlng = e.target.getLatLng();
          setFieldValue('Latitude', latlng.lat?.toFixed(6));
          setFieldValue('Longitude', latlng.lng?.toFixed(6));
        },
      }}
    >
      <Popup>Selected Location</Popup>
    </Marker>
  ) : null;
};

const FactoryLocation = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [FactoryLocationTableData, setFactoryLocationTableData] = useState([]);
  const [factories, setFactories] = useState([]);

  const [FactoryLocations, setFactoryLocations] = useState([]);
  const [UserId, setUserId] = useState(null);

  const [initialValue, setInitialValue] = useState(FactoryLocationInitialValue);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openMapPicker, setOpenMapPicker] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  // snackbar context
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // api fetch data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    GetAllHelpData();
    getAllFactoryLocation();
  }, []);

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        return {
          Latitude: latitude,
          Longitude: longitude,
        };
      },
      (err) => {
        console.log('Error getting location: ', err.message);
      }
    );
  };

  const GetAllHelpData = async () => {
    try {
      showLoading();
      const resData = await axios.get('ReferenceData/GetAllFactories');
      setFactories(resData.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  };

  const FactoryLocationValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        Factory: Yup.object()
          .required('Select a factory')
          .test('unique-factory', 'This factory already has a location assigned', (value) => {
            if (!value || !value.AccId) return true; // skip if no factory selected\
            const selectedFactoryId = Number(value.AccId);
            const initialFactoryId = Number(initialValue.Factory.AccId);
            return !FactoryLocations.filter((loc) => Number(loc.FactoryId) !== initialFactoryId).some((loc) => {
              const locFactoryId = Number(loc.FactoryId);
              return locFactoryId === selectedFactoryId;
            });
          }),
        Latitude: Yup.number()
          .typeError('Latitude must be a number')
          .required('Latitude is required')
          .min(-90, 'Latitude must be between -90 and 90')
          .max(90, 'Latitude must be between -90 and 90'),
        Longitude: Yup.number()
          .typeError('Longitude must be a number')
          .required('Longitude is required')
          .min(-180, 'Longitude must be between -180 and 180')
          .max(180, 'Longitude must be between -180 and 180'),
      }),
    [FactoryLocations, initialValue]
  );

  async function getAllFactoryLocation() {
    try {
      showLoading();
      const response = await axios.get('Master/FactoryLocation/GetAllFactoryLocation');
      setFactoryLocations(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  async function InsertFactoryLocation(values) {
    try {
      showLoading();
      const payload = {
        Latitude: values.Latitude,
        Longitude: values.Longitude,
        FactoryId: values.Factory.AccId,
        UserId,
      };
      await axios.post('Master/FactoryLocation/AddFactoryLocation', payload);
      getAllFactoryLocation();
      openSnackBar('Factory Location Added Successfully!');
      // setFilterData(filterInitialValue);
      setEdit(false);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
    setOpen(false);
  }

  const UpdateFactoryLocation = async (values) => {
    try {
      showLoading();
      const payload = {
        FactoryLocationId: values.FactoryLocationId,
        Latitude: values.Latitude,
        Longitude: values.Longitude,
        FactoryId: values.Factory.AccId,
        UserId,
      };
      await axios.post(`Master/FactoryLocation/UpdateFactoryLocation`, payload);
      getAllFactoryLocation();
      setInitialValue(FactoryLocationInitialValue);
      openSnackBar('Factory Location Updated Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    setOpen(false);
    setEdit(false);
    hideLoading();
  };

  const DeleteFactoryLocation = async () => {
    try {
      showLoading();
      await axios.delete(`Master/FactoryLocation/DeleteFactoryLocation/${deleteId}`);
      const newFactoryLocation = FactoryLocations.filter(
        (FactoryLocation) => FactoryLocation.FactoryLocationId !== deleteId
      );
      setFactoryLocations(newFactoryLocation);
      setFactoryLocationTableData(newFactoryLocation);
      openSnackBar('Factory Location Deleted Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    handleCloseDelete();
    setDeleteId(null);
    hideLoading();
  };

  const handleFilterDataChange = (name, value) => {
    setFilterData({ ...filterData, [name]: value });
  };
  useEffect(() => {
    const newTableData = FactoryLocations.filter((item) =>
      item.Factory?.trim()?.toLowerCase()?.includes(filterData?.searchTerm?.toLowerCase())
    );
    if (page > 0) setPage(0);
    setFactoryLocationTableData(newTableData);
  }, [FactoryLocations, filterData]);

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleCloseDialog = () => {
    setOpen(!open);
    setEdit(false);
    setInitialValue(FactoryLocationInitialValue);
  };

  const handleEdit = (item) => {
    setEdit(true);
    setOpen(true);
    setInitialValue({
      FactoryLocationId: item.FactoryLocationId,
      Factory: factories.find((f) => Number(f.AccId) === Number(item.FactoryId)) || null,
      Latitude: item.Latitude,
      Longitude: item.Longitude,
    });
  };

  // pagination
  const { emptyRows, totalRows, currentPage } = useMemo(() => {
    const totalRows = FactoryLocationTableData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const currentPage = Math.max(0, Math.min(page, totalPages - 1));
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, totalRows - page * rowsPerPage);

    return { emptyRows, totalRows, currentPage };
  }, [FactoryLocationTableData, page, rowsPerPage]);

  return (
    <>
      <Container maxWidth={'xl'} sx={{ pt: 2 }} elevation={3}>
        {/* table header */}
        <Grid justifyContent={'space-between'} alignItems={'center'} spacing={2}>
          <Typography
            variant="h5"
            p={1}
            boxShadow={1}
            borderColor="#11235A"
            textAlign={'center'}
            borderRadius={2}
            pl={2}
            color="white"
            sx={{
              // borderRadius: 50,
              background: '#47A992',
              boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
            }}
          >
            <Typography variant="h6" color="white" className="font_style1" textAlign={'center'}>
              FACTORY LOCATION MASTER
            </Typography>
          </Typography>
        </Grid>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={'center'}
          spacing={2}
          my={3}
          px={3}
        >
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Search"
            size="small"
            onChange={(e) => {
              handleFilterDataChange('searchTerm', e.target.value);
            }}
            value={filterData.searchTerm}
          />
          <Button
            variant="outlined"
            color="secondary"
            sx={{ boxShadow: 1 }}
            onClick={() => setOpen(!open)}
            startIcon={<PlaylistAddIcon />}
          >
            Add Factory Location
          </Button>
        </Stack>
        <TableContainer sx={{ borderRadius: '7px', maxHeight: '450px' }} component={Paper} elevation={2}>
          <Table className="table-bordered" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">S.No</StyledTableCell>
                <StyledTableCell align="left">Factory</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? FactoryLocationTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : FactoryLocationTableData
              ).map((item, i) => (
                <TableRow key={item.Factory} hover>
                  <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                  {/* <TableCell align="center">{item.SNo}</TableCell> */}
                  <TableCell align="left">{item.Factory}</TableCell>
                  <TableCell align="center" sx={{ padding: '0' }}>
                    <IconButton aria-label="Edit" onClick={() => handleEdit(item)}>
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => {
                        setDeleteId(item.FactoryLocationId);
                        setOpenDelete(true);
                      }}
                    >
                      <DeleteForeverIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={currentPage} // Use the calculated currentPage value
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0); // Reset the page when rowsPerPage changes
          }}
          mt={2}
        />
        <Grid m={2}>
          {/* add new popup form dialog box */}
          <Dialog maxWidth={'sm'} open={open} onClose={handleCloseDialog} fullWidth>
            <Container component={Paper} elevation={2} style={{ padding: '7px 14px' }}>
              <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mr={1}>
                <Typography variant="h6" textAlign={'left'} pl={2} fontWeight={600} color="primary">
                  {edit ? 'Edit' : 'Add'} Factory Location
                </Typography>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack sx={{ p: 1 }}>
                <Formik
                  validationSchema={FactoryLocationValidationSchema}
                  initialValues={initialValue}
                  onSubmit={async (values, { resetForm }) => {
                    if (edit) await UpdateFactoryLocation(values);
                    else await InsertFactoryLocation(values);
                    resetForm();
                  }}
                >
                  {({ submitForm, values, errors, handleChange, handleBlur, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                      <Grid container spacing={2} p={1} rowSpacing={3}>
                        <Grid item xs={12}>
                          <Autocomplete
                            options={factories}
                            getOptionLabel={(factory) => factory.AccName}
                            isOptionEqualToValue={(value) => value.AccId === values.Factory.AccId}
                            renderOption={(props, factory) => (
                              <li {...props} key={factory.AccId}>
                                {factory.AccName}
                              </li>
                            )}
                            value={values.Factory || null}
                            name="Factory"
                            onChange={(event, value) => {
                              setFieldValue('Factory', value);
                            }}
                            size="small"
                            onBlur={handleBlur}
                            renderInput={(params) => (
                              <TextField
                                required
                                {...params}
                                label="Factory"
                                error={touched.Factory && !!errors.Factory}
                                helperText={touched.Factory && errors.Factory}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <TextField
                            fullWidth
                            type="text"
                            variant="outlined"
                            color="secondary"
                            label="Latitude"
                            size="small"
                            name="Latitude"
                            value={values.Latitude}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.Latitude && !!errors.Latitude}
                            helperText={touched.Latitude && errors.Latitude}
                            InputLabelProps={{ shrink: !!values.Latitude }}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <TextField
                            fullWidth
                            type="text"
                            variant="outlined"
                            color="secondary"
                            label="Longitude"
                            size="small"
                            name="Longitude"
                            value={values.Longitude}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.Longitude && !!errors.Longitude}
                            helperText={touched.Longitude && errors.Longitude}
                            InputLabelProps={{ shrink: !!values.Longitude }}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1} ml={1}>
                          <Chip
                            label=" Pick Location on Map"
                            color="primary"
                            icon={<LocationOnIcon fontSize="small" />}
                            onClick={() => setOpenMapPicker(true)}
                            clickable
                            variant="outlined"
                            sx={{
                              fontSize: '0.875rem',
                              px: 2,
                              py: 1,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      </Grid>

                      <Stack direction="row" justifyContent="center" gap={2} sx={{ textAlign: 'center', mt: 3 }}>
                        <Button
                          onClick={submitForm}
                          disabled={isSubmitting}
                          fullWidth
                          variant="contained"
                          color={edit ? 'warning' : 'primary'}
                          size="small"
                          sx={{ boxShadow: 1 }}
                        >
                          {edit ? 'Update' : 'Save'}
                        </Button>
                      </Stack>
                      <Dialog
                        open={openMapPicker}
                        onClose={() => setOpenMapPicker(false)}
                        fullWidth
                        maxWidth="lg"
                        PaperProps={{ sx: { height: '90vh' } }}
                      >
                        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                          {/* Close Button in top-right corner */}
                          <IconButton
                            aria-label="close"
                            onClick={() => setOpenMapPicker(false)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1000,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>

                          <MapContainer
                            center={
                              values.Latitude && values.Longitude
                                ? [values.Latitude, values.Longitude]
                                : [20.5937, 78.9629]
                            }
                            zoom={values.Longitude ? 14 : 5}
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <LocationMarker values={values} setFieldValue={setFieldValue} />

                            {/* Add this component to handle the geocoder */}
                            <GeocoderControl
                              position="topleft"
                              onMarkGeocode={(result) => {
                                const { lat, lng } = result.geocode.center;
                                setFieldValue('Latitude', lat.toFixed(6));
                                setFieldValue('Longitude', lng.toFixed(6));
                              }}
                            />
                          </MapContainer>
                        </Box>
                      </Dialog>
                    </Form>
                  )}
                </Formik>
              </Stack>
            </Container>
          </Dialog>
        </Grid>

        {/* confirmation dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete} fullScreen={fullScreen} fullWidth maxWidth="xs">
          <DialogTitle id="responsive-dialog-title">Are you sure you want to delete?</DialogTitle>
          <DialogActions>
            <Button variant="contained" autoFocus onClick={handleCloseDelete} color="primary">
              Cancel
            </Button>
            <Button variant="contained" onClick={DeleteFactoryLocation} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default FactoryLocation;
